import React, { useState } from 'react';
import { X, Instagram, Loader2, Plus } from 'lucide-react';
import { translations } from '../../translations';
import { validateInstagramUsername, isValidInstagramChar } from '../../utils/validation';
import { hikerApi } from '../../services/hikerApi';
import { supabase } from '../../lib/supabase';
import { imageService } from '../../services/imageService';

interface AddAccountModalProps {
  onClose: () => void;
  onAdd: (accountData: any) => Promise<void>;
  isDarkMode: boolean;
  language: string;
}

export function AddAccountModal({ onClose, onAdd, isDarkMode, language }: AddAccountModalProps) {
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = translations[language as keyof typeof translations];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username.trim()) {
      setError('Username is required');
      return;
    }

    if (!validateInstagramUsername(username)) {
      setError(t.invalid_username_format);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        setError('User not authenticated');
        return;
      }

      const cleanUsername = username.replace('@', '').toLowerCase().trim();

      // Check if account already exists
      const { data: existingAccounts, error: queryError } = await supabase
        .from('instagram_accounts')
        .select('id, connection_status')
        .eq('user_id', userData.user.id)
        .eq('username', '@' + cleanUsername);

      if (queryError) throw queryError;

      const activeAccount = existingAccounts?.find(acc => acc.connection_status !== 'failed');
      if (activeAccount) {
        setError(t.account_already_exists);
        return;
      }

      // Get account info from Instagram
      const result = await hikerApi.getAccountInfo(cleanUsername);
      
      if (result.error || !result.data) {
        setError(result.message || 'Failed to fetch account information');
        return;
      }

      const initialFollowers = parseInt(result.data.followers || '0', 10);

      let avatarUrl = result.data.profilePicUrl;
      if (avatarUrl && avatarUrl !== 'https://via.placeholder.com/150') {
        avatarUrl = await imageService.uploadAvatar(avatarUrl, cleanUsername);
      }

      const accountData = {
        username: '@' + cleanUsername,
        avatar: avatarUrl,
        followers: result.data.followers || '0',
        posts: result.data.posts || 0,
        name: result.data.fullName || cleanUsername,
        user_id: userData.user.id,
        is_active: true,
        subscription_status: 'not_started',
        initial_followers: initialFollowers,
        actual_followers: initialFollowers,
        follower_gained: 0,
        engagement: '0%',
        follower_history: [{
          date: new Date().toISOString(),
          count: initialFollowers,
          gains: 0
        }],
        is_private: result.data.isPrivate || false,
        is_verified: result.data.isVerified || false,
        biography: result.data.biography || '',
        profile_pic_url_hd: result.data.profilePicUrl || '',
        follower_count: initialFollowers,
        following_count: parseInt(result.data.following || '0', 10),
        media_count: result.data.posts || 0,
        account_type: result.data.isBusiness ? 2 : 1,
        category_name: result.data.businessCategory || null,
        category: result.data.businessCategory || null,
        country_code: 'France',
        connection_status: null,
        is_legacy_client: 'NEW'
      };

      await onAdd(accountData);
      onClose();
    } catch (err) {
      console.error('Error adding account:', err);
      setError(err instanceof Error ? err.message : 'Failed to add account');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUsernameChange = (value: string) => {
    setUsername(value);
    setError(null);
  };

  const handleUsernameKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key.length !== 1) return;
    
    if (!isValidInstagramChar(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className={`w-full max-w-md rounded-lg p-6 ${
        isDarkMode ? 'bg-[#1c1c1c]' : 'bg-white'
      }`}>
        <div className="flex justify-between items-center mb-6">
          <h3 className={`text-xl font-semibold ${
            isDarkMode ? 'text-gray-200' : 'text-gray-900'
          }`}>
            {t.add_account}
          </h3>
          <button 
            onClick={onClose}
            className={`p-1 rounded-full ${
              isDarkMode ? 'hover:bg-[#2b2b2b]' : 'hover:bg-gray-100'
            }`}
          >
            <X size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-500 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {t.username}
            </label>
            <div className="relative">
              <input
                type="text"
                value={username}
                onChange={(e) => handleUsernameChange(e.target.value)}
                onKeyPress={handleUsernameKeyPress}
                placeholder="your_username"
                placeholder={t.username_placeholder || "your_username"}
                maxLength={31}
                className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm ${
                  isDarkMode
                    ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                    : 'bg-gray-50 text-gray-900 border-gray-200'
                } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                required
              />
              <Instagram size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 rounded-lg text-sm ${
                isDarkMode 
                  ? 'text-gray-300 hover:bg-[#2b2b2b]' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              disabled={isLoading || !username.trim()}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  {t.adding || "Adding..."}
                </>
              ) : (
                <>
                  <Plus size={14} />
                  {t.add_account}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}